import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { indexDTFAtom } from '@/state/dtf/atoms'
import { chainIdAtom, devModeAtom } from '@/state/atoms'
import { isAddress } from '@/utils'
import {
  ExplorerDataType,
  getExplorerLink,
} from '@/utils/getExplorerLink'
import useTokenList from '@/hooks/use-token-list'
import { Token } from '@/types'
import { Trans, useLingui } from '@lingui/react/macro'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { AlertTriangle, ArrowUpRight, DownloadCloud, FilePlus2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  isUnitBasketAtom,
  proposedIndexBasketAtom,
  proposedSharesAtom,
  proposedUnitsAtom,
} from '../../atoms'

const MAX_FILE_SIZE = 1024 * 1024 // 1MB

const setNewBasketFromCsvAtom = atom(
  null,
  (
    get,
    set,
    {
      csv,
      tokenListMap,
    }: { csv: string; tokenListMap: Record<string, Token> | null }
  ) => {
    const proposedSharesMap = get(proposedSharesAtom)
    const proposedUnitsMap = get(proposedUnitsAtom)
    const proposedIndexBasket = get(proposedIndexBasketAtom)

    if (!proposedIndexBasket) return []

    const rows = csv.split('\n')
    const newProposedIndexBasket = { ...proposedIndexBasket }
    const newProposedShares = { ...proposedSharesMap }
    const newProposedUnits = { ...proposedUnitsMap }
    const skipped: { symbol: string; address: string }[] = []

    // Track which addresses are present in the CSV
    const csvAddresses = new Set<string>()

    // Skip header row and process each data row
    rows.slice(1).forEach((row) => {
      if (!row.trim()) return // Skip empty rows

      const values = row.split(',')
      if (values.length < 3) return // Ensure we have enough columns

      const symbol = values[0].trim()
      const address = values[1].trim().toLowerCase()
      const valueStr = values[2].trim() || '0'

      // Validate data
      if (
        !address ||
        !isAddress(address) ||
        !valueStr ||
        isNaN(Number(valueStr))
      ) {
        return
      }

      // Skip token if not found in tokenList
      const tokenData = tokenListMap?.[address]
      if (!tokenData) {
        skipped.push({ symbol, address })
        return
      }

      csvAddresses.add(address)

      // Add token to basket if it doesn't exist
      if (!newProposedIndexBasket[address]) {
        newProposedIndexBasket[address] = {
          token: tokenData,
          currentShares: '0',
          currentUnits: '0',
        }
      }

      // Update shares and units
      // Convert scientific notation to decimal string if present
      const normalizedValue = valueStr.toLowerCase().includes('e')
        ? Number.parseFloat(valueStr.toString()).toString()
        : valueStr

      newProposedShares[address] = normalizedValue
      newProposedUnits[address] = normalizedValue
    })

    // Set assets in current basket but not in CSV to 0
    for (const address of Object.keys(proposedIndexBasket)) {
      if (!csvAddresses.has(address)) {
        newProposedShares[address] = '0'
        newProposedUnits[address] = '0'
      }
    }

    // Update atoms with new values
    set(proposedIndexBasketAtom, newProposedIndexBasket)
    set(proposedSharesAtom, newProposedShares)
    set(proposedUnitsAtom, newProposedUnits)

    return skipped
  }
)

const BasketCsvSetup = () => {
  const { t } = useLingui()
  const dtf = useAtomValue(indexDTFAtom)
  const isUnitBasket = useAtomValue(isUnitBasketAtom)
  const assets = useAtomValue(proposedIndexBasketAtom)
  const chainId = useAtomValue(chainIdAtom)
  const isDevMode = useAtomValue(devModeAtom)
  const { data: tokenList } = useTokenList(chainId, { unfiltered: true })
  const setNewBasketFromCsv = useSetAtom(setNewBasketFromCsvAtom)
  const [error, setError] = useState<string | null>(null)
  const [skippedTokens, setSkippedTokens] = useState<
    { symbol: string; address: string }[]
  >([])

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null)
      setSkippedTokens([])

      if (!tokenList) {
        setError(t`Loading token list... please try again`)
        return
      }

      if (rejectedFiles.length > 0) {
        setError(t`Please upload a CSV file less than 1MB.`)
        return
      }

      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]

      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const csvText = event.target?.result as string
          if (!csvText) {
            setError(t`Failed to read CSV file`)
            return
          }

          // Create token map from tokenList
          const tokenListMap = tokenList.reduce(
            (acc, token) => {
              acc[token.address.toLowerCase()] = token
              return acc
            },
            {} as Record<string, Token>
          )

          // Update the basket state with proper token data
          const skipped = setNewBasketFromCsv({ csv: csvText, tokenListMap })
          setSkippedTokens(skipped ?? [])
        } catch (err) {
          console.error('Error parsing CSV:', err)
          setError(t`Failed to parse CSV file. Please check the format.`)
        }
      }

      reader.onerror = () => {
        setError(t`Error reading the file`)
      }

      reader.readAsText(file)
    },
    [tokenList, setNewBasketFromCsv, setSkippedTokens, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
  })

  const handleTemplate = (e: React.MouseEvent) => {
    e.stopPropagation()
    let template = `symbol,address,value`

    if (assets) {
      template += `\n${Object.values(assets)
        .map(
          (asset) =>
            `${asset.token.symbol},${asset.token.address},${isUnitBasket ? asset.currentUnits : asset.currentShares}`
        )
        .join('\n')}`
    }

    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)

    // Create a temporary anchor element to download the file
    const a = document.createElement('a')
    a.href = url
    a.download = `${dtf?.token.symbol}_basket_template.csv`
    document.body.appendChild(a)
    a.click()

    // Clean up
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'flex items-center gap-2 p-4 border border-dashed rounded-xl',
          isDragActive && 'border-primary bg-muted/50',
          error && 'border-destructive'
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div className="rounded-full border border-foreground p-2">
          <FilePlus2 size={16} />
        </div>
        <div className="text-base mr-auto">
          <p className="font-bold">
            <Trans>Replace Basket with CSV</Trans>
          </p>
          <p>
            <span className="text-primary cursor-pointer">
              <Trans>Select a CSV file to upload</Trans>
            </span>{' '}
            <span className="text-legend">
              <Trans>or drag and drop it here</Trans>
            </span>
          </p>
        </div>
        <Button
          variant="ghost"
          className="text-legend font-normal gap-2"
          onClick={handleTemplate}
        >
          <Trans>CSV Template</Trans>
          <DownloadCloud size={14} />
        </Button>
      </div>
      {isDevMode && skippedTokens.length > 0 && (
        <Alert
          variant="warning"
          className="rounded-xl bg-warning/10 border-warning/20"
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            <Trans>Tokens not available</Trans>
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {skippedTokens.map((t) => (
                <li key={t.address}>
                  <span className="font-medium">{t.symbol}</span>{' '}
                  <a
                    href={getExplorerLink(
                      t.address,
                      chainId,
                      ExplorerDataType.ADDRESS
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 font-mono text-xs hover:text-foreground"
                  >
                    {t.address.slice(0, 6)}...{t.address.slice(-4)}
                    <ArrowUpRight size={12} strokeWidth={1.5} />
                  </a>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

export default BasketCsvSetup
